import { Directive, ElementRef, Input, OnInit, Renderer2, OnChanges, SimpleChanges } from '@angular/core';
import { IncidentFieldFlags } from '../models/incident';

@Directive({
  selector: '[appWhatsappField]',
  standalone: true
})
export class WhatsappFieldDirective implements OnInit, OnChanges {
  @Input() appWhatsappField: string = ''; // The field name to check
  @Input() whatsappFlags: IncidentFieldFlags | null = null; // The flags object

  private iconElement: HTMLElement | null = null;
  private wrapperElement: HTMLElement | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.updateFieldStyle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['whatsappFlags'] || changes['appWhatsappField']) {
      this.updateFieldStyle();
    }
  }

  private updateFieldStyle(): void {
    if (!this.whatsappFlags || !this.appWhatsappField) {
      this.removeWhatsappIndicator();
      return;
    }

    const fieldName = this.appWhatsappField as keyof IncidentFieldFlags;
    const isFromWhatsapp = this.whatsappFlags[fieldName] === true;

    if (isFromWhatsapp) {
      this.addWhatsappIndicator();
    } else {
      this.removeWhatsappIndicator();
    }
  }

  private addWhatsappIndicator(): void {
    // Add light green border
    this.renderer.setStyle(this.el.nativeElement, 'border-color', '#90EE90');
    this.renderer.setStyle(this.el.nativeElement, 'border-width', '2px');

    // Check if we already have an icon wrapper
    if (this.wrapperElement) {
      return;
    }

    // Create wrapper for the input with icon
    const parent = this.el.nativeElement.parentNode;
    if (!parent) return;

    // Create a container that will hold the icon and the input
    this.wrapperElement = this.renderer.createElement('div');
    this.renderer.setStyle(this.wrapperElement, 'position', 'relative');
    this.renderer.setStyle(this.wrapperElement, 'display', 'inline-block');
    this.renderer.setStyle(this.wrapperElement, 'width', '100%');

    // Create the WhatsApp icon
    this.iconElement = this.renderer.createElement('span');
    this.renderer.setStyle(this.iconElement, 'position', 'absolute');
    this.renderer.setStyle(this.iconElement, 'left', '8px');
    this.renderer.setStyle(this.iconElement, 'top', '50%');
    this.renderer.setStyle(this.iconElement, 'transform', 'translateY(-50%)');
    this.renderer.setStyle(this.iconElement, 'color', '#25D366');
    this.renderer.setStyle(this.iconElement, 'font-size', '18px');
    this.renderer.setStyle(this.iconElement, 'z-index', '1');
    this.renderer.setStyle(this.iconElement, 'pointer-events', 'none');

    // Create the WhatsApp SVG icon
    const svgIcon = this.createWhatsAppIcon();
    this.renderer.appendChild(this.iconElement, svgIcon);

    // Add padding to the input to make room for the icon
    this.renderer.setStyle(this.el.nativeElement, 'padding-left', '32px');

    // Insert wrapper and icon
    this.renderer.insertBefore(parent, this.wrapperElement, this.el.nativeElement);
    this.renderer.appendChild(this.wrapperElement, this.iconElement);
    this.renderer.appendChild(this.wrapperElement, this.el.nativeElement);
  }

  private removeWhatsappIndicator(): void {
    // Reset border
    this.renderer.removeStyle(this.el.nativeElement, 'border-color');
    this.renderer.removeStyle(this.el.nativeElement, 'border-width');
    this.renderer.removeStyle(this.el.nativeElement, 'padding-left');

    // Remove icon and wrapper if they exist
    if (this.wrapperElement && this.wrapperElement.parentNode) {
      const parent = this.wrapperElement.parentNode;
      this.renderer.insertBefore(parent, this.el.nativeElement, this.wrapperElement);
      this.renderer.removeChild(parent, this.wrapperElement);
      this.wrapperElement = null;
      this.iconElement = null;
    }
  }

  private createWhatsAppIcon(): SVGElement {
    // Create WhatsApp icon using SVG
    const svg = this.renderer.createElement('svg', 'svg');
    this.renderer.setAttribute(svg, 'viewBox', '0 0 24 24');
    this.renderer.setAttribute(svg, 'width', '18');
    this.renderer.setAttribute(svg, 'height', '18');
    this.renderer.setAttribute(svg, 'fill', 'currentColor');

    const path = this.renderer.createElement('path', 'svg');
    this.renderer.setAttribute(path, 'd', 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z');

    this.renderer.appendChild(svg, path);
    return svg;
  }
}
